// Pieces shared by the RFQ Files panel and the per-line cert list: the query,
// the link that opens a file, and the upload queue.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRfqDocumentUrl, listRfqDocuments, type RfqDocument, type RfqDocumentKind } from '@/lib/api';
import { DocumentLink } from '@/components/document-link';
import { errorMessage, uploadRfqFile } from '@/lib/rfq-files';

export const rfqDocumentsKey = (rfqPublicId: string) => ['rfq-documents', rfqPublicId] as const;

export function useRfqDocuments(rfqPublicId: string) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: rfqDocumentsKey(rfqPublicId),
    queryFn: () => listRfqDocuments(rfqPublicId, () => getToken()),
    staleTime: 10_000,
  });
}

function fileTitle(doc: RfqDocument): string {
  return [doc.filename, doc.sha256 ? `SHA-256: ${doc.sha256}` : null, doc.note].filter(Boolean).join('\n');
}

/** Opens a file in a new tab (PDFs, images) or downloads it (everything else). */
export function RfqFileLink({ rfqPublicId, doc, className }: { rfqPublicId: string; doc: RfqDocument; className?: string }) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkClass = className ?? 'text-primary hover:underline font-medium truncate max-w-full';

  if (doc.legacy) {
    return (
      <DocumentLink objectKey={doc.legacy.objectKey} label={doc.filename} filename={doc.filename}
        title={fileTitle(doc)} className={linkClass} />
    );
  }

  async function open() {
    if (!doc.id || loading) return;
    setError(null);
    setLoading(true);
    // Claim the tab before the await, or the popup blocker eats it.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url } = await getRfqDocumentUrl(rfqPublicId, doc.id, false, () => getToken());
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <span className="block min-w-0 max-w-full">
      <button type="button" onClick={open} disabled={loading} title={fileTitle(doc)}
        className={`${linkClass} block max-w-full text-left disabled:opacity-60`}>
        {loading ? 'Opening…' : doc.filename}
      </button>
      {error && <span className="block text-xs text-destructive">{error}</span>}
    </span>
  );
}

export interface QueuedUpload {
  key: string;
  name: string;
  progress: number;
  status: 'uploading' | 'verifying' | 'failed';
  error?: string;
}

/**
 * Uploads files three at a time and refreshes the RFQ's list as each lands.
 * Failed rows stay visible with their reason until dismissed.
 */
export function useRfqUploadQueue(rfqPublicId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [queue, setQueue] = useState<QueuedUpload[]>([]);

  const patch = (key: string, p: Partial<QueuedUpload>) =>
    setQueue((q) => q.map((u) => (u.key === key ? { ...u, ...p } : u)));

  async function uploadOne(key: string, file: File, kind: RfqDocumentKind, lineItemId: string | null) {
    try {
      await uploadRfqFile({
        rfqPublicId, file, kind, lineItemId, tokenGetter: () => getToken(),
        onProgress: (f) => patch(key, { progress: f, status: f >= 1 ? 'verifying' : 'uploading' }),
      });
      setQueue((q) => q.filter((u) => u.key !== key));
      qc.invalidateQueries({ queryKey: rfqDocumentsKey(rfqPublicId) });
    } catch (err) {
      patch(key, { status: 'failed', error: errorMessage(err) });
    }
  }

  async function add(items: { file: File; kind: RfqDocumentKind; lineItemId: string | null }[]) {
    const tagged = items.map((it) => ({ ...it, key: `${Date.now()}-${Math.random().toString(36).slice(2)}` }));
    setQueue((q) => [...q, ...tagged.map((t) => ({ key: t.key, name: t.file.name, progress: 0, status: 'uploading' as const }))]);
    const pending = [...tagged];
    const worker = async () => {
      for (let t = pending.shift(); t; t = pending.shift()) await uploadOne(t.key, t.file, t.kind, t.lineItemId);
    };
    await Promise.all([worker(), worker(), worker()]);
  }

  const dismiss = (key: string) => setQueue((q) => q.filter((u) => u.key !== key));
  return { queue, add, dismiss };
}

export function UploadQueueList({ queue, onDismiss }: { queue: QueuedUpload[]; onDismiss: (key: string) => void }) {
  if (!queue.length) return null;
  return (
    <ul className="space-y-2 text-xs">
      {queue.map((u) => (
        <li key={u.key} className="rounded-md border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-medium" title={u.name}>{u.name}</span>
            {u.status === 'failed' ? (
              <button type="button" onClick={() => onDismiss(u.key)}
                className="shrink-0 rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                Dismiss
              </button>
            ) : (
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {u.status === 'verifying' ? 'Verifying…' : `${Math.round(u.progress * 100)}%`}
              </span>
            )}
          </div>
          {u.status === 'failed' ? (
            <p className="mt-1 break-words text-destructive">{u.error}</p>
          ) : (
            <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-muted">
              <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(u.progress * 100)}%` }} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
